/**
 * Global Review Prompt Overlay
 *
 * Mounts once after auth resolves and polls /api/reviews/pending-prompts.
 * Renders a centered formal modal (dark overlay, blocks clicks) split into:
 *   • Orange top section — avatar + heading + subtitle (matches user's mockup)
 *   • White bottom section — interactive 5-star rating, comment textarea,
 *     Rate Later + Submit Review buttons
 *
 * Behaviour:
 *   • Hidden on /login, /register, /admin/*, /claim/*, /
 *   • Polls on mount + every 5 minutes + on route change
 *   • Submit posts directly to /api/reviews — no navigation away
 *   • Rate Later / X / backdrop → POST /api/reviews/prompts/{id}/snooze
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Star, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min

const SUPPRESS_PATHS = [/^\/login/, /^\/register/, /^\/admin\b/, /^\/claim\//, /^\/$/];
const isSuppressed = (pathname) => SUPPRESS_PATHS.some((re) => re.test(pathname));

export default function ReviewPromptOverlay() {
  const { user } = useContext(AuthContext) || {};
  const location = useLocation();
  const [prompts, setPrompts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  const fetchPrompts = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/reviews/pending-prompts`, { withCredentials: true });
      setPrompts(r.data?.prompts || []);
    } catch {
      /* silent */
    }
  }, [user]);

  // Initial + on route change
  useEffect(() => {
    if (!user) return;
    if (isSuppressed(location.pathname)) return;
    fetchPrompts();
  }, [user, location.pathname, fetchPrompts]);

  // Poll every 5 min
  useEffect(() => {
    if (!user) return;
    const id = setInterval(fetchPrompts, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, fetchPrompts]);

  const current = prompts[0];

  // Reset form when current prompt changes
  useEffect(() => {
    setRating(0);
    setHoverRating(0);
    setComment('');
  }, [current?.id]);

  const handleSnooze = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/reviews/prompts/${current.id}/snooze`, {}, { withCredentials: true });
      setPrompts((p) => p.slice(1));
    } catch {
      /* silent */
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!current || busy || rating < 1) return;
    setBusy(true);
    try {
      const payload = {
        review_type: current.kind, // 'customer_to_tradie' | 'tradie_to_customer'
        reviewee_id: current.reviewee_id,
        rating,
        comment: comment.trim(),
        booking_id: current.booking_id || current.project_id || undefined,
      };
      await axios.post(`${API}/reviews/create`, payload, { withCredentials: true });
      toast.success('Thanks — your review has been submitted!');
      // Backend auto-closes the matching review_request; pop locally
      setPrompts((p) => p.slice(1));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to submit review';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  // ───────────────────── RENDER GUARDS ─────────────────────
  if (!user || !current) return null;
  if (isSuppressed(location.pathname)) return null;

  const isCustomer = current.kind === 'customer_to_tradie';
  const heading = isCustomer ? 'How was your experience?' : 'How was your customer?';
  const subtitle = isCustomer
    ? `Tell others how ${current.reviewee_name || 'this tradie'} did`
    : `Help fellow tradies — review ${current.reviewee_name || 'this customer'}`;

  const initials = (current.reviewee_name || '?')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const displayedRating = hoverRating || rating;
  const submitDisabled = busy || rating < 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-4 animate-in fade-in duration-200"
      data-testid="review-prompt-overlay"
      onClick={(e) => e.target === e.currentTarget && handleSnooze()}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        data-testid="review-prompt-card"
      >
        {/* ─── Orange top section ─── */}
        <div className="bg-mlt-orange p-6 text-white relative">
          <button
            type="button"
            aria-label="Close"
            disabled={busy}
            onClick={handleSnooze}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition disabled:opacity-50"
            data-testid="review-prompt-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white text-mlt-orange flex items-center justify-center font-bold text-xl border-4 border-white/40 shrink-0 overflow-hidden">
              {current.reviewee_photo ? (
                <img src={current.reviewee_photo} alt={current.reviewee_name || ''} className="w-full h-full object-cover" />
              ) : (
                <span data-testid="review-prompt-avatar-initials">{initials}</span>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg leading-tight" data-testid="review-prompt-heading">
                {heading}
              </h3>
              <p className="text-sm text-white/90 truncate" data-testid="review-prompt-subtitle">
                {subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* ─── White bottom section ─── */}
        <div className="bg-white p-6 space-y-4">
          {/* Interactive 5-star strip */}
          <div className="flex items-center justify-center gap-1.5" data-testid="review-prompt-stars">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
                onClick={() => setRating(s)}
                onMouseEnter={() => setHoverRating(s)}
                onMouseLeave={() => setHoverRating(0)}
                className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-mlt-orange focus:ring-offset-2 rounded p-0.5"
                data-testid={`review-prompt-star-${s}`}
              >
                <Star
                  className={`w-9 h-9 ${
                    s <= displayedRating ? 'text-mlt-orange' : 'text-mlt-border-strong'
                  }`}
                  fill={s <= displayedRating ? 'currentColor' : 'none'}
                  strokeWidth={1.5}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm font-medium text-mlt-navy" data-testid="review-prompt-rating-label">
              {['Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating - 1]}
            </p>
          )}

          {/* Comment textarea */}
          <div>
            <label htmlFor="review-comment" className="sr-only">
              Share your experience
            </label>
            <textarea
              id="review-comment"
              data-testid="review-prompt-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 1000))}
              placeholder={
                isCustomer
                  ? 'Share your experience with this tradie (optional)…'
                  : 'How was working with this customer? (optional)'
              }
              rows={3}
              className="w-full rounded-lg border border-mlt-border bg-mlt-off-white p-3 text-sm text-mlt-text-primary placeholder:text-mlt-text-muted focus:border-mlt-orange focus:outline-none focus:ring-1 focus:ring-mlt-orange resize-none"
            />
            <div className="flex justify-end">
              <span className="text-xs text-mlt-text-muted" data-testid="review-prompt-comment-counter">
                {comment.length}/1000
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={handleSnooze}
              className="flex-1 border-mlt-border text-mlt-text-primary hover:bg-mlt-off-white"
              data-testid="review-prompt-snooze-btn"
            >
              Rate Later
            </Button>
            <Button
              disabled={submitDisabled}
              onClick={handleSubmit}
              className="flex-1 bg-mlt-orange hover:bg-mlt-orange-hover text-white font-semibold disabled:opacity-50"
              data-testid="review-prompt-submit-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Submit Review'}
            </Button>
          </div>

          {/* Footer cadence note */}
          <p className="text-xs text-mlt-text-muted text-center" data-testid="review-prompt-footer">
            {current.phase === 'monthly'
              ? "We'll check in once a month while this stays open."
              : "We'll check back next week if you'd like to skip for now."}
          </p>
        </div>
      </div>
    </div>
  );
}
